import React, { useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import { Calendar, RefreshCw, Printer, Clock, Users, CheckCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOperationsApi, transformRequestToOperation } from '../../services/operationsApi';
import { enqueueSnackbar } from 'notistack';
import { OperationsHeader } from '../operations/OperationsHeader';
import { OperationsStats } from '../operations/OperationsStats';
import { OperationsFilters } from '../operations/OperationsFilters';
import { CheckInList } from '../operations/CheckInList';
import { CheckOutList } from '../operations/CheckOutList';
import { EarlyLateModal } from '../operations/EarlyLateModal';
import { ContactGuestModal } from '../operations/ContactGuestModal';
import { NoShowModal } from '../operations/NoShowModal';
import { AddPaymentModal } from '../operations/AddPaymentModal';
import { PrintOperationsModal } from '../operations/PrintOperationsModal';
import { calculateOperationsStats, filterOperations } from '../../utils/operationsData';
import { CheckoutManagementPage } from '../CheckoutManagementPage/CheckoutManagementPage';
import { CheckoutRequestModal } from '../checkout/CheckoutRequestModal';
import { useApi } from '../Store/ApiContext';

export const DailyOperationsPage = () => {
  const { getData, responseApi } = useApi();
  const { t } = useTranslation();
  const {
    getDailyOperations,
    updateRequestStatus,
    handleEarlyLateOperation: apiEarlyLate,
    handleNoShowOperation: apiNoShow
  } = useOperationsApi();

  // State management
  const [selectedDate, setSelectedDate] = useState(() => moment().format('YYYY-MM-DD'));
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});

  // Modal states
  const [showEarlyLateModal, setShowEarlyLateModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [earlyLateType, setEarlyLateType] = useState('early');
  const [contactInfo, setContactInfo] = useState({ phone: '', email: '', reqClientName: '' });

  // Checkout modal state
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedRequestForCheckout, setSelectedRequestForCheckout] = useState(null);
  const [bookingPolicies, setBookingPolicies] = useState({});

  useEffect(() => {
    // add title page
    document.title = t("operationsTitle");
  }, []);


  // Load booking policies from backend
  const loadBookingPolicies = async () => {
    try {
      const bookingPolicies = sessionStorage.getItem("bookingPolicies");
      if (bookingPolicies) {
        setBookingPolicies(JSON.parse(bookingPolicies));
      } else {
        const response = await getData(null, 'bookingPolicies');
        if (response && response.results) {
          setBookingPolicies(response.results.bookingPolicies);
          sessionStorage.setItem("bookingPolicies", JSON.stringify(response.results.bookingPolicies));
        }
      }
    } catch (error) {
      console.error('Error loading booking policies:', error);
      enqueueSnackbar(t('error.loadingFailed') || 'Failed to load policies', { variant: 'error' });
    }
  };

  useEffect(() => {
    loadBookingPolicies();
  }, []);

  // Load operations for selected date
  useEffect(() => {
    const loadOperations = async () => {
      setLoading(true);
      try {
        const response = await getDailyOperations(selectedDate);
        if (response?.results) {
          // Transform backend data to frontend format
          const transformedOperations = response.results.map(request =>
            transformRequestToOperation(request, selectedDate)
          );
          setOperations(transformedOperations);
        } else {
          setOperations([]);
        }
      } catch (error) {
        console.error('Error loading operations:', error);
        enqueueSnackbar(t('errorLoadingOperations'), { variant: 'error' });
        setOperations([]);
      } finally {
        setLoading(false);
      }
    };

    loadOperations();
  }, [selectedDate]);

  // Filter operations
  const filteredOperations = useMemo(() => {
    return filterOperations(operations, filters);
  }, [operations, filters]);

  // Separate check-ins and check-outs
  const checkIns = filteredOperations.filter(op => op.type === 'checkin');
  const checkOuts = filteredOperations.filter(op => op.type === 'checkout');

  // Calculate statistics
  const stats = useMemo(() => {
    return calculateOperationsStats(filteredOperations);
  }, [filteredOperations]);

  // Action handlers
  const handleConfirmOperation = async (operationId, operationType) => {
    try {
      const actualTime = moment().format('HH:mm');
      const response = await updateRequestStatus(operationId, 'confirmed', { operationType });

      if (response?.message === 'success') {
        setOperations(prev =>
          prev.map(op =>
            op.id === operationId
              ? { ...op, operationStatus: 'confirmed', operationType }
              : op
          )
        );
        enqueueSnackbar(t('successOperationConfirmed'), { variant: 'success' });
      } else {
        throw new Error(response?.message || 'Failed to confirm operation');
      }
    } catch (error) {
      console.error('Error confirming operation:', error);
      enqueueSnackbar(t('errorUpdatingOperation'), { variant: 'error' });
    }
  };

  const handleEarlyLateOperation = (operationId, type) => {
    const operation = operations.find(op => op.id === operationId);

    console.log(operation);

    if (operation) {
      setSelectedOperation(operation);
      setEarlyLateType(type);
      setShowEarlyLateModal(true);

      // checkout late / early request
      setShowCheckoutModal(true);
      setSelectedRequestForCheckout(operation.rawRequest);
    }
  };

  const handleNoShowOperation = (operationId) => {
    const operation = operations.find(op => op.id === operationId);
    if (operation) {
      setSelectedOperation(operation);
      setShowNoShowModal(true);
    }
  };

  const handleContactGuest = (phone, email) => {
    const operation = operations.find(op => op.phone === phone);
    setContactInfo({
      phone,
      email: email || '',
      reqClientName: operation?.reqClientName || '',
      orgNameInvoice: operation?.rawRequest.organization?.orgNameInvoice || '',
    });
    setShowContactModal(true);
  };

  const handleEarlyLateConfirm = async (request) => {
    try {
      const response = await apiEarlyLate(
        request.operationId,
        request.type,
        request.requestedTime,
        request.reason
      );

      if (response?.message === 'success') {
        setOperations(prev =>
          prev.map(op =>
            op.id === request.operationId
              ? {
                ...op,
                status: request.type,
                actualTime: request.requestedTime,
                notes: request.reason
              }
              : op
          )
        );
        enqueueSnackbar(t('successOperationUpdated'), { variant: 'success' });
      } else {
        throw new Error(response?.message || 'Failed to update operation');
      }
    } catch (error) {
      console.error('Error updating early/late operation:', error);
      enqueueSnackbar(t('errorUpdatingOperation'), { variant: 'error' });
    }

    setShowEarlyLateModal(false);
    setSelectedOperation(null);
  };

  const handleNoShowConfirm = async (operationId, noShowingReason, releaseRoom) => {
    try {
      const response = await apiNoShow(operationId, noShowingReason, releaseRoom);

      if (response?.message === 'success') {
        setOperations(prev =>
          prev.map(op =>
            op.id === operationId
              ? {
                ...op,
                operationStatus: 'noShow',
                noShowingReason,
                // roomStatus: releaseRoom ? 'clean' : op.roomStatus
              }
              : op
          )
        );
        enqueueSnackbar(t('successOperationUpdated'), { variant: 'success' });
      } else {
        throw new Error(response?.message || 'Failed to update operation');
      }
    } catch (error) {
      console.error('Error updating noShow operation:', error);
      enqueueSnackbar(t('errorUpdatingOperation'), { variant: 'error' });
    }

    setShowNoShowModal(false);
    setSelectedOperation(null);
  };

  const handleAddPayment = (operationId) => {
    const operation = operations.find(op => op.id === operationId);
    if (operation) {
      setSelectedOperation(operation);
      setShowAddPaymentModal(true);
    }
  };

  const handleAddPaymentConfirm = async (operationId, paymentData) => {
    try {      
      const response = await responseApi('patch', paymentData, `operations/${operationId}/addPayment`);

      if (response?.message === 'success' || response?.message === 'Payment added successfully') {
        enqueueSnackbar(t('paymentAddedSuccessfully'), { variant: 'success' });
        
        // Refresh operations to get updated data
        await handleRefresh();
      } else {
        enqueueSnackbar(t('errorAddingPayment'), { variant: 'error' });
      }
    } catch (error) {
      console.error('Error adding payment:', error);
      enqueueSnackbar(t('errorAddingPayment'), { variant: 'error' });
    }

    setShowAddPaymentModal(false);
    setSelectedOperation(null);
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await getDailyOperations(selectedDate);
      if (response?.results) {
        const transformedOperations = response.results.map(request =>
          transformRequestToOperation(request, selectedDate)
        );
        setOperations(transformedOperations);
      } else {
        setOperations([]);
      }
      enqueueSnackbar(t('successOperationUpdated'), { variant: 'success' });
    } catch (error) {
      console.error('Error refreshing operations:', error);
      enqueueSnackbar(t('errorLoadingOperations'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    setShowPrintModal(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  return (
    <div className={`bg-babyblue-50 big-card container-fluid`}>

      <div className="pt-4 pb-3 px-2 mx-3">
        <div className="">
          {/* Header */}
          <OperationsHeader
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            onRefresh={handleRefresh}
            onPrint={handlePrint}
            loading={loading}
          />

          {/* Statistics */}
          <OperationsStats stats={stats} />

          {/* Filters */}
          <OperationsFilters
            filters={filters}
            operations={operations}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />

          {/* Operations Lists */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            {/* Check-ins */}
            <CheckInList
              operations={checkIns}
              loading={loading}
              onConfirm={handleConfirmOperation}
              onEarlyLate={handleEarlyLateOperation}
              onNoShow={handleNoShowOperation}
              onContact={handleContactGuest}
              onAddPayment={handleAddPayment}
            />

            {/* Check-outs */}
            <CheckOutList
              operations={checkOuts}
              loading={loading}
              onConfirm={handleConfirmOperation}
              onEarlyLate={handleEarlyLateOperation}
              onNoShow={handleNoShowOperation}
              onContact={handleContactGuest}
              onAddPayment={handleAddPayment}
            />
          </div>
        </div>

        {/* Modals */}
        {/* <EarlyLateModal
          isOpen={showEarlyLateModal}
          onClose={() => {
            setShowEarlyLateModal(false);
            setSelectedOperation(null);
          }}
          operation={selectedOperation}
          type={earlyLateType}
          onConfirm={handleEarlyLateConfirm}
        /> */}

        <ContactGuestModal
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
          reqClientName={contactInfo.reqClientName}
          phone={contactInfo.phone}
          email={contactInfo.email}
          orgNameInvoice={contactInfo.orgNameInvoice}
        />

        <NoShowModal
          isOpen={showNoShowModal}
          onClose={() => {
            setShowNoShowModal(false);
            setSelectedOperation(null);
          }}
          operation={selectedOperation}
          onConfirm={handleNoShowConfirm}
        />

        <AddPaymentModal
          isOpen={showAddPaymentModal}
          onClose={() => {
            setShowAddPaymentModal(false);
            setSelectedOperation(null);
          }}
          operation={selectedOperation}
          onConfirm={handleAddPaymentConfirm}
        />

        <PrintOperationsModal
          isOpen={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          operations={filteredOperations}
          selectedDate={selectedDate}
          stats={stats}
        />

        {selectedRequestForCheckout && (
          <CheckoutRequestModal
            isOpen={showCheckoutModal}
            onClose={() => {
              setShowCheckoutModal(false);
              setSelectedRequestForCheckout(null);
            }}
            withConfirm={(operationId, checkoutRequest) => {
              setOperations(prev =>
                prev.map(op =>
                  op.id === operationId
                    ? {
                      ...op,
                      checkoutRequest
                    }
                    : op
                )
              );
            }}
            request={selectedRequestForCheckout}
            bookingPolicies={bookingPolicies}
          />
        )}
      </div>
    </div>
  );
};